import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["visible", "children", "appearanceMode"],
    _excluded2 = ["isHidden", "children", "onOpen", "onClose", "appearanceMode", "zIndex"],
    _excluded3 = ["smContent", "content", "target", "smHeader", "smAppearanceMode", "popoverProps", "onSheetOpened", "onSheetClosed", "onPopoverOpened", "onPopoverClosed", "PopoverComponent", "zIndex"];
import React, { useContext, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import styled, { ThemeContext } from 'styled-components';
import Breakpoint from '../Breakpoint';
import Box from '../Box';
import * as Popover from '../Popover';
import CloseThinIcon from '../Icons/CloseThinIcon';
import { canUseDOM } from '../utils/environment';
import { useAppearanceFocus } from '../hooks';
import { Fade, SlideUp } from '../animations';
import { isFunction, isSmallViewport, noop, useThemeDefaults } from '../utils';
import { OpenStateContext, OpenStateContextProvider } from '../OpenState';
import { PreventScrollingLegacy } from '../utils/preventScrolling';
var SheetLayout = styled(Box).withConfig({
  displayName: "Sheet__SheetLayout",
  componentId: "sc-1jg6dms-0"
})(function (_ref) {
  var theme = _ref.theme;
  return {
    position: 'fixed',
    zIndex: 10,
    padding: 0,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    overflow: 'auto',
    background: theme.colors[theme.sheet.bg]
  };
});
var SheetContent = styled(Box).withConfig({
  displayName: "Sheet__SheetContent",
  componentId: "sc-1jg6dms-1"
})(["display:flex;height:100%;overflow:hidden;flex-direction:column;"]);

var getAnimationLayerStyles = function getAnimationLayerStyles(_ref2) {
  var zIndex = _ref2.zIndex,
      theme = _ref2.theme;
  return {
    position: 'fixed',
    zIndex: typeof zIndex !== 'undefined' ? zIndex : theme.sheet.zIndex,
    width: '100%',
    height: '100%',
    top: 0,
    left: 0
  };
};

var SlideUpStyled = styled(SlideUp).withConfig({
  displayName: "Sheet__SlideUpStyled",
  componentId: "sc-1jg6dms-2"
})(["", ""], getAnimationLayerStyles);
var FadeStyled = styled(Fade).withConfig({
  displayName: "Sheet__FadeStyled",
  componentId: "sc-1jg6dms-3"
})(["", ""], getAnimationLayerStyles);

var LightBoxAnimated = function LightBoxAnimated(_ref3) {
  var visible = _ref3.visible,
      children = _ref3.children,
      _ref3$appearanceMode = _ref3.appearanceMode,
      appearanceMode = _ref3$appearanceMode === void 0 ? 'Fade' : _ref3$appearanceMode,
      props = _objectWithoutProperties(_ref3, _excluded);

  if (appearanceMode === 'SlideUp') {
    return /*#__PURE__*/React.createElement(SlideUpStyled, _extends({
      visible: visible
    }, props), children);
  }

  return /*#__PURE__*/React.createElement(FadeStyled, _extends({
    visible: visible
  }, props), children);
};

var LightBox = /*#__PURE__*/React.memo(function (_ref4) {
  var isHidden = _ref4.isHidden,
      children = _ref4.children,
      _ref4$onOpen = _ref4.onOpen,
      onOpen = _ref4$onOpen === void 0 ? noop : _ref4$onOpen,
      _ref4$onClose = _ref4.onClose,
      onClose = _ref4$onClose === void 0 ? noop : _ref4$onClose,
      _ref4$appearanceMode = _ref4.appearanceMode,
      appearanceMode = _ref4$appearanceMode === void 0 ? 'Fade' : _ref4$appearanceMode,
      zIndex = _ref4.zIndex,
      props = _objectWithoutProperties(_ref4, _excluded2);

  return canUseDOM() ? /*#__PURE__*/ReactDOM.createPortal( /*#__PURE__*/React.createElement(LightBoxAnimated, {
    visible: !isHidden,
    appearanceMode: appearanceMode,
    "aria-modal": true,
    onEnter: onOpen,
    onExit: onClose,
    zIndex: zIndex
  }, /*#__PURE__*/React.createElement(SheetLayout, props, /*#__PURE__*/React.createElement(SheetContent, null, children))), document.body) : null;
});
var HeaderNavBar = useThemeDefaults(function (_ref5) {
  var theme = _ref5.theme;
  return {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    borderBottom: "".concat(theme.borderWidth, " solid"),
    borderColor: theme.sheet.lightbox.header.borderColor,
    padding: theme.sheet.lightbox.header.padding
  };
}, Box);
export var SheetHeader = /*#__PURE__*/React.memo(function (_ref6) {
  var _ref6$withNavBar = _ref6.withNavBar,
      withNavBar = _ref6$withNavBar === void 0 ? true : _ref6$withNavBar,
      leftButton = _ref6.leftButton,
      rightButton = _ref6.rightButton,
      children = _ref6.children;
  var focusableRef = useRef(null);
  var Wrapper = !withNavBar ? React.Fragment : HeaderNavBar;

  var _useContext = useContext(OpenStateContext),
      hide = _useContext.hide,
      hidden = _useContext.hidden;

  useAppearanceFocus(!hidden, focusableRef);
  return /*#__PURE__*/React.createElement(Wrapper, null, leftButton && leftButton({
    hide: hide,
    focusableRef: focusableRef
  }), rightButton && rightButton({
    hide: hide,
    focusableRef: focusableRef
  }), !(leftButton || leftButton) && children({
    hide: hide,
    focusableRef: focusableRef
  }));
});
var HideIcon = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var theme = useContext(ThemeContext);
  return /*#__PURE__*/React.createElement(Box, {
    textAlign: "left",
    onClick: props.hide,
    cursor: "pointer"
  }, /*#__PURE__*/React.createElement(CloseThinIcon, {
    ref: ref,
    color: theme.colors.label,
    mt: theme.sheet.lightbox.icon.topOffset,
    mb: theme.sheet.lightbox.icon.topOffset,
    ml: theme.sheet.lightbox.icon.leftOffset,
    width: theme.sheet.lightbox.icon.width,
    height: theme.sheet.lightbox.icon.height
  }));
});

var AppearanceComponent = function AppearanceComponent(_ref7) {
  var children = _ref7.children,
      _ref7$onCreate = _ref7.onCreate,
      onCreate = _ref7$onCreate === void 0 ? noop : _ref7$onCreate,
      _ref7$onDestroy = _ref7.onDestroy,
      onDestroy = _ref7$onDestroy === void 0 ? noop : _ref7$onDestroy;
  // in case of changing onCreate and onDestroy functions
  // we should take care about it without any additional calling of the effect
  var onCreateRef = useRef(onCreate);
  var onDestroyRef = useRef(onDestroy);
  onCreateRef.current = onCreate;
  onDestroyRef.current = onDestroy;
  useEffect(function () {
    if (isFunction(onCreateRef.current)) {
      onCreateRef.current();
    }

    return function () {
      if (isFunction(onDestroyRef.current)) {
        onDestroyRef.current();
      }
    };
  }, []);
  return children;
};

var _ref10 = /*#__PURE__*/React.createElement(PreventScrollingLegacy, null);

export var Sheet = /*#__PURE__*/React.memo(function (_ref8) {
  var smContent = _ref8.smContent,
      content = _ref8.content,
      _target = _ref8.target,
      smHeader = _ref8.smHeader,
      _ref8$smAppearanceMod = _ref8.smAppearanceMode,
      smAppearanceMode = _ref8$smAppearanceMod === void 0 ? 'Fade' : _ref8$smAppearanceMod,
      _ref8$popoverProps = _ref8.popoverProps,
      popoverProps = _ref8$popoverProps === void 0 ? {} : _ref8$popoverProps,
      _ref8$onSheetOpened = _ref8.onSheetOpened,
      onSheetOpened = _ref8$onSheetOpened === void 0 ? noop : _ref8$onSheetOpened,
      _ref8$onSheetClosed = _ref8.onSheetClosed,
      onSheetClosed = _ref8$onSheetClosed === void 0 ? noop : _ref8$onSheetClosed,
      _ref8$onPopoverOpened = _ref8.onPopoverOpened,
      onPopoverOpened = _ref8$onPopoverOpened === void 0 ? noop : _ref8$onPopoverOpened,
      _ref8$onPopoverClosed = _ref8.onPopoverClosed,
      onPopoverClosed = _ref8$onPopoverClosed === void 0 ? noop : _ref8$onPopoverClosed,
      _ref8$PopoverComponen = _ref8.PopoverComponent,
      PopoverComponent = _ref8$PopoverComponen === void 0 ? Popover.BaseDropdown : _ref8$PopoverComponen,
      zIndex = _ref8.zIndex,
      props = _objectWithoutProperties(_ref8, _excluded3);

  return /*#__PURE__*/React.createElement(OpenStateContextProvider, null, function (_ref9) {
    var hide = _ref9.hide,
        show = _ref9.show,
        setHidden = _ref9.setHidden,
        isHidden = _ref9.isHidden;
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Breakpoint, {
      ssr: true
    }, function (breakpoint) {
      return /*#__PURE__*/React.createElement(React.Fragment, null, isSmallViewport(breakpoint) && /*#__PURE__*/React.createElement(AppearanceComponent, {
        onCreate: function onCreate() {
          if (!isHidden) {
            onSheetOpened();
          }
        },
        onDestroy: function onDestroy() {
          if (!isHidden) {
            onSheetClosed();
          }
        }
      }, _target({
        show: show,
        hide: hide,
        setHidden: setHidden,
        isHidden: isHidden
      }), smContent && /*#__PURE__*/React.createElement(React.Fragment, null, !isHidden && _ref10, /*#__PURE__*/React.createElement(LightBox, _extends({
        onOpen: onSheetOpened,
        onClose: onSheetClosed,
        isHidden: isHidden,
        appearanceMode: smAppearanceMode,
        zIndex: zIndex
      }, props), isFunction(smHeader) ? smHeader({
        hide: hide
      }) : /*#__PURE__*/React.createElement(SheetHeader, {
        withNavBar: false
      }, function (_ref11) {
        var hide = _ref11.hide,
            focusableRef = _ref11.focusableRef;
        return /*#__PURE__*/React.createElement(HideIcon, {
          hide: hide,
          ref: focusableRef
        });
      }), isFunction(smContent) && /*#__PURE__*/React.createElement(Box, {
        height: "100%",
        overflow: "auto"
      }, smContent({
        hide: hide
      }))))), !isSmallViewport(breakpoint) && /*#__PURE__*/React.createElement(AppearanceComponent, {
        onCreate: function onCreate() {
          if (!isHidden) {
            onPopoverOpened();
          }
        },
        onDestroy: function onDestroy() {
          if (!isHidden) {
            onPopoverClosed();
          }
        }
      }, /*#__PURE__*/React.createElement(Popover.ClickAwayOpenState, {
        onClickAway: hide
      }, function (_ref12) {
        var containerRef = _ref12.containerRef;
        return /*#__PURE__*/React.createElement(Box, {
          display: "inline"
        }, /*#__PURE__*/React.createElement(PopoverComponent, _extends({
          placement: "bottom",
          ref: containerRef,
          target: function target(_ref13) {
            var ref = _ref13.ref;
            return _target({
              ref: ref,
              show: show,
              hide: hide,
              setHidden: setHidden,
              isHidden: isHidden
            });
          },
          onOpen: onPopoverOpened,
          onClose: onPopoverClosed,
          hidden: isHidden
        }, popoverProps, props), isFunction(content) && content({
          hide: hide
        })));
      })));
    }));
  });
});